import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Lock } from 'lucide-react';
import {
  House, Tray, FolderSimple, UsersThree, GearSix, Plus, CaretDown, CaretRight, MagnifyingGlass,
  SignOut, Buildings, CaretUpDown, ChartBar, ListChecks, CreditCard, Lifebuoy, DotsThree,
  SidebarSimple, UserCircle, FileText, ArrowLineLeft, Sun, Moon, Sparkle, Check, Plugs, Cube, BookOpen,
} from '@phosphor-icons/react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import NotificationBell from './NotificationBell';
import { ClientLogo } from './ClientLogo';
import UpgradeButton from './UpgradeButton';

export function Sidebar({ onCreateProject }) {
  const { user, logout, currentOrg, switchOrg, getOrgHeaders, API } = useAuth();
  const subCtx = useSubscription();
  const caps = subCtx?.caps || {};
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [portals, setPortals] = useState([]);
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [serviceDeskOpen, setServiceDeskOpen] = useState(true);
  // Project id of the task the user is currently viewing on /browse/<KEY>
  // or /tasks/<id>. Set by TaskDetailPage via a `task-context` window event
  // so the sidebar can highlight the parent project even though the URL
  // doesn't include the project id. Cleared when the task page unmounts.
  const [activeTaskProjectId, setActiveTaskProjectId] = useState(null);
  // Jira-style sidebar collapse — when true the sidebar shrinks to icon-only,
  // freeing the main canvas. State persists across reloads via localStorage.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('iw_sidebar_collapsed') === '1'; } catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed(v => {
      const next = !v;
      try { localStorage.setItem('iw_sidebar_collapsed', next ? '1' : '0'); } catch {}
      return next;
    });
  };
  // Fully hide the sidebar (advanced full-screen mode). A small floating
  // button lets users bring it back. Persisted separately from `collapsed`.
  const [hidden, setHidden] = useState(() => {
    try { return localStorage.getItem('iw_sidebar_hidden') === '1'; } catch { return false; }
  });
  const setHiddenPersist = (val) => {
    setHidden(val);
    try { localStorage.setItem('iw_sidebar_hidden', val ? '1' : '0'); } catch {}
  };
  // Global app appearance theme (light | dark | branded). Shared with the
  // rest of the shell via ThemeContext.
  const { mode: theme, setMode: setTheme } = useTheme();

  const memberRole = currentOrg?.role;
  const canCreateProject = ['owner', 'admin', 'project_manager'].includes(memberRole);
  // Contributors are workers, not admins — they shouldn't see Insights,
  // Contact Support, or the org-Settings page. Backend continues to enforce
  // these capabilities; this just removes the entry points from the UI.
  const isContributor = memberRole === 'contributor';
  // Insights is a leadership view — owner / admin / project_manager only.
  // Contributors and viewers don't see the entry point. Project-managers
  // still see only their own projects; that scoping is enforced by the
  // backend (visibleProjectIdsForHome / project_members table).
  const canSeeInsights = ['owner', 'admin', 'project_manager'].includes(memberRole);
  // Service Desk is agent-facing. Only show it to people who can actually use
  // it: owners/admins (who manage everything) OR members staffed as an agent
  // on at least one portal. A plain contributor/viewer who was never added to
  // any portal must NOT see the Service Desk section or its Queue. The portals
  // API is already agent-scoped (returns [] for non-agents), so length>0 means
  // "this user is an agent somewhere".
  const canSeeServiceDesk = caps.service_desk_enabled === true
    && (['owner', 'admin'].includes(memberRole) || portals.length > 0);
  const otherOrgs = user?.memberships?.filter(m => m.org_id !== currentOrg?.org_id) || [];

  useEffect(() => {
    if (!currentOrg) return;
    axios.get(`${API}/projects`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setProjects(r.data)).catch(() => {});
    const h = () => axios.get(`${API}/projects`, { withCredentials: true, headers: getOrgHeaders() }).then(r => setProjects(r.data)).catch(() => {});
    window.addEventListener('project-created', h);
    window.addEventListener('project-deleted', h);
    return () => {
      window.removeEventListener('project-created', h);
      window.removeEventListener('project-deleted', h);
    };
  }, [currentOrg]);

  // Load portals when Service Desk is enabled so the sidebar can render
  // each portal as a quick-link under the SERVICE DESK section.
  useEffect(() => {
    if (!currentOrg || caps.service_desk_enabled !== true) { setPortals([]); return; }
    const loadPortals = () => axios.get(`${API}/service-desk/portals`, { withCredentials: true, headers: getOrgHeaders() })
      .then(r => setPortals(r.data || []))
      .catch(() => setPortals([]));
    loadPortals();
    window.addEventListener('sd-portals-changed', loadPortals);
    return () => window.removeEventListener('sd-portals-changed', loadPortals);
  }, [currentOrg, caps.service_desk_enabled, API]);

  // Listen for the task-page hint so we can highlight the project that
  // contains the currently-open task (e.g., on /browse/PM-2 the sidebar's
  // "Program Management" row should look active even though the URL has
  // no project id in it).
  useEffect(() => {
    const onTaskCtx = (e) => setActiveTaskProjectId(e?.detail?.project_id || null);
    window.addEventListener('task-context', onTaskCtx);
    return () => window.removeEventListener('task-context', onTaskCtx);
  }, []);
  // Defensive: also clear the highlight whenever the route leaves /browse or
  // /tasks entirely, so the indicator can't get stuck on stale data.
  useEffect(() => {
    const onTaskRoute = location.pathname.startsWith('/browse/') || location.pathname.startsWith('/tasks/');
    if (!onTaskRoute && activeTaskProjectId) setActiveTaskProjectId(null);
  }, [location.pathname, activeTaskProjectId]);

  const navItems = [
    { label: 'Home', icon: House, path: '/dashboard' },
    { label: 'My Tasks', icon: ListChecks, path: '/my-tasks' },
    { label: 'Documents', icon: FileText, path: '/documents' },
    ...(['owner', 'admin', 'project_manager'].includes(memberRole) ? [{ label: 'Clients', icon: Buildings, path: '/clients' }] : []),
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const PROJECT_COLORS = ['bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-indigo-500'];
  const getProjectColor = (idx) => PROJECT_COLORS[idx % PROJECT_COLORS.length];

  // ─── Fully hidden (advanced full-screen) — only a floating reopen handle ───
  if (hidden) {
    return (
      <button
        onClick={() => setHiddenPersist(false)}
        title="Show sidebar"
        data-testid="sidebar-reopen-btn"
        className="sb fixed top-3 left-3 z-50 w-9 h-9 rounded-lg bg-[var(--sb-bg)] text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] ring-1 ring-[var(--sb-ring)] shadow-lg flex items-center justify-center hover:bg-[var(--sb-surface)] transition-colors"
        data-theme={theme}
      >
        <SidebarSimple size={16} weight="bold" />
      </button>
    );
  }

  // ─── Collapsed (Jira-style icon-only rail) ─────────────────
  if (collapsed) {
    return (
      <aside className="sb w-[56px] h-screen bg-[var(--sb-bg)] border-r border-[var(--sb-border)] flex flex-col select-none transition-[width] duration-200" data-theme={theme} data-testid="sidebar-collapsed">
        {/* Brand mark — tapping the logo also expands, but we render a
            dedicated expand button below so the affordance is explicit. */}
        <button
          onClick={toggleCollapsed}
          title="Expand sidebar"
          className="w-full h-14 flex items-center justify-center border-b border-[var(--sb-border)] bg-[var(--sb-surface)] hover:bg-[var(--sb-hover)] transition-colors"
          data-testid="sidebar-brand-collapsed-btn"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_f2e3da60-dd27-470d-bf43-dd90a294a7a5/artifacts/los1nant_WhatsApp%20Image%202026-06-02%20at%2014.31.36.jpeg"
            alt="Inspiro Projects"
            className="w-8 h-8 rounded-md bg-black object-cover ring-1 ring-[var(--sb-ring)]"
            data-testid="sidebar-brand-collapsed"
          />
        </button>
        {/* Dedicated expand toggle — mirrors the collapse button shown in the
            expanded sidebar, so users have an obvious affordance instead of
            having to know the logo is clickable. */}
        <button
          onClick={toggleCollapsed}
          title="Expand sidebar"
          className="mx-2 mt-2 h-8 rounded-md flex items-center justify-center text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)] transition-colors"
          data-testid="sidebar-toggle"
        >
          <SidebarSimple size={14} weight="bold" />
        </button>
        <button
          onClick={() => setHiddenPersist(true)}
          title="Hide sidebar (full screen)"
          className="mx-2 mt-1 h-8 rounded-md flex items-center justify-center text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)] transition-colors"
          data-testid="sidebar-hide-collapsed"
        >
          <ArrowLineLeft size={14} weight="bold" />
        </button>
        {/* Notification bell + workspace avatar */}
        <div className="px-2 pt-2 flex flex-col items-center gap-2">
          <NotificationBell />
          <div className="w-full aspect-square rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[12px] font-bold" title={currentOrg?.org_name}>
            {currentOrg?.org_name?.charAt(0)?.toUpperCase() || 'W'}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden mt-2 sidebar-scroll">
          <div className="px-2 space-y-0.5">
            {navItems.map(item => {
              const isDocs = item.path === '/documents';
              const docsLocked = isDocs && caps.workspace_docs === false;
              return (
                <button key={item.path} title={docsLocked ? `${item.label} (Business plan)` : item.label}
                  onClick={() => navigate(docsLocked ? '/billing' : item.path)}
                  className={`w-full aspect-square flex items-center justify-center rounded-md transition-colors duration-150 relative
                    ${isActive(item.path) ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}-collapsed`}>
                  <item.icon size={16} weight={isActive(item.path) ? 'fill' : 'regular'} />
                  {docsLocked && <Lock size={9} className="absolute top-0.5 right-0.5 text-amber-400/80" />}
                </button>
              );
            })}
            {canSeeInsights && (
              <button title="Insights" onClick={() => navigate('/insights')}
                className={`w-full aspect-square flex items-center justify-center rounded-md transition-colors duration-150
                  ${isActive('/insights') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}
                data-testid="nav-insights-collapsed">
                <ChartBar size={16} weight={isActive('/insights') ? 'fill' : 'regular'} />
              </button>
            )}
          </div>
          {/* Project icons */}
          <div className="px-2 mt-3 pt-3 border-t border-[var(--sb-border)] space-y-1">
            {projects.map((p, idx) => {
              const active = isActive(`/projects/${p.project_id}`) || p.project_id === activeTaskProjectId;
              return (
                <button key={p.project_id} title={p.name}
                  onClick={() => navigate(`/projects/${p.project_id}`)}
                  className={`w-full aspect-square rounded-md flex items-center justify-center transition-colors
                    ${active ? 'ring-2 ring-blue-400/70' : 'hover:opacity-80 ring-1 ring-[var(--sb-ring)]'} ${getProjectColor(idx)}`}
                  data-testid={`sidebar-project-collapsed-${p.project_id}`}>
                  <span className="text-[10px] font-bold text-white">{p.key?.charAt(0)}</span>
                </button>
              );
            })}
            {canCreateProject && (
              <button title="New project" onClick={onCreateProject}
                className="w-full aspect-square rounded-md flex items-center justify-center text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)] transition-colors"
                data-testid="sidebar-create-project-collapsed">
                <Plus size={14} weight="bold" />
              </button>
            )}
          </div>
          {!isContributor && (
            <div className="px-2 mt-3 pt-3 border-t border-[var(--sb-border)] space-y-1">
              {canSeeServiceDesk && (
                <button title="Service Desk" onClick={() => navigate('/service-desk')}
                  className={`w-full aspect-square flex items-center justify-center rounded-md transition-colors
                    ${isActive('/service-desk') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}
                  data-testid="nav-service-desk-collapsed">
                  <Lifebuoy size={16} weight={isActive('/service-desk') ? 'fill' : 'regular'} />
                </button>
              )}
              <button title="Members" onClick={() => navigate('/team')}
                className={`w-full aspect-square flex items-center justify-center rounded-md transition-colors
                  ${isActive('/team') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                <UsersThree size={16} weight={isActive('/team') ? 'fill' : 'regular'} />
              </button>
            </div>
          )}
        </div>
        {/* User */}
        <div className="p-2 border-t border-[var(--sb-border)] bg-[var(--sb-surface)]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger-collapsed" className="w-full aspect-square flex items-center justify-center rounded-md hover:bg-[var(--sb-hover)] transition-colors">
                <Avatar className="h-7 w-7">
                  <AvatarImage src={user?.avatar} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-[10px] font-semibold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => navigate('/account')} className="text-sm"><UserCircle size={14} className="mr-2" /> My Account</DropdownMenuItem>
              {['owner', 'admin'].includes(currentOrg?.role) && (
                <DropdownMenuItem onClick={() => navigate('/settings')} className="text-sm"><GearSix size={14} className="mr-2" /> Workspace Settings</DropdownMenuItem>
              )}
              {['owner', 'admin'].includes(currentOrg?.role) && (
                <DropdownMenuItem onClick={() => navigate('/billing')} className="text-sm" data-testid="nav-billing"><CreditCard size={14} className="mr-2" /> Billing &amp; Plans</DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[11px] text-zinc-400 font-normal py-1">Appearance</DropdownMenuLabel>
              {[{ key: 'light', label: 'Light', Icon: Sun }, { key: 'dark', label: 'Dark', Icon: Moon }, { key: 'branded', label: 'Branded', Icon: Sparkle }].map(opt => (
                <DropdownMenuItem key={opt.key} onClick={(e) => { e.preventDefault(); setTheme(opt.key); }} className="text-sm" data-testid={`sidebar-theme-${opt.key}-collapsed`}>
                  <opt.Icon size={14} className="mr-2" /> {opt.label}
                  {theme === opt.key && <Check size={13} weight="bold" className="ml-auto text-blue-600" />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-700 text-sm"><SignOut size={14} className="mr-2" /> Sign Out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>
    );
  }

  return (
    <aside className="sb w-[240px] h-screen bg-[var(--sb-bg)] border-r border-[var(--sb-border)] flex flex-col select-none transition-[width] duration-200 sidebar-scroll" data-theme={theme} data-testid="sidebar">
      {/* Single brand strip — App name + tagline on the left, Notification
          bell + collapse toggle on the right. The workspace identity is now
          shown only in the user pill at the bottom (Linear / Notion pattern)
          to avoid duplicating brand info at the top. */}
      <div className="px-3 pt-3 pb-3 border-b border-[var(--sb-border)] bg-[var(--sb-surface)] flex items-center gap-2" data-testid="sidebar-brand">
        <img
          src="https://customer-assets.emergentagent.com/job_f2e3da60-dd27-470d-bf43-dd90a294a7a5/artifacts/los1nant_WhatsApp%20Image%202026-06-02%20at%2014.31.36.jpeg"
          alt="Inspiro Projects"
          className="w-8 h-8 rounded-lg bg-black object-cover ring-1 ring-[var(--sb-ring)] flex-shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="font-['Outfit'] text-[13px] font-bold text-[var(--sb-text)] tracking-tight leading-tight whitespace-nowrap" data-testid="sidebar-brand-name">Inspiro Projects</p>
          <p className="text-[9.5px] text-[var(--sb-text-dim)] leading-tight tracking-wide">Project Management</p>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0" data-testid="sidebar-actions">
          <NotificationBell />
          <button onClick={toggleCollapsed} title="Collapse to icons"
            className="w-7 h-7 rounded-md flex items-center justify-center text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)] transition-colors"
            data-testid="sidebar-toggle">
            <SidebarSimple size={14} weight="bold" />
          </button>
          <button onClick={() => setHiddenPersist(true)} title="Hide sidebar (full screen)"
            className="w-7 h-7 rounded-md flex items-center justify-center text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)] transition-colors"
            data-testid="sidebar-hide">
            <ArrowLineLeft size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden sidebar-scroll">
        <div className="px-2 pt-2 pb-4">
          {/* Primary Nav */}
          <div className="space-y-0.5">
            {navItems.map((item) => {
              const active = isActive(item.path);
              // Documents is gated behind a paid plan (workspace_docs cap).
              // Free orgs see the row with a lock icon and a tooltip; click
              // routes them to /billing instead of /documents.
              const isDocs = item.path === '/documents';
              const docsLocked = isDocs && caps.workspace_docs === false;
              return (
                <button key={item.path} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  onClick={() => navigate(docsLocked ? '/billing' : item.path)}
                  title={docsLocked ? 'Documents is a Business-plan feature — upgrade to unlock' : undefined}
                  className={`group relative w-full flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] font-medium transition-colors duration-150
                    ${active ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                  {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-blue-400" aria-hidden />}
                  <item.icon size={16} weight={active ? 'fill' : 'regular'} />
                  <span className="flex-1 text-left">{item.label}</span>
                  {docsLocked && <Lock size={11} className="text-amber-400/80" data-testid={`nav-${item.label.toLowerCase()}-lock`} />}
                </button>
              );
            })}
          </div>

          {/* Insights — only visible to owner / admin / project_manager */}
          {canSeeInsights && (
            <div className="mt-5">
              <button onClick={() => navigate('/insights')} data-testid="nav-insights"
                className={`group relative w-full flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] font-medium transition-colors duration-150
                  ${isActive('/insights') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                {isActive('/insights') && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-blue-400" aria-hidden />}
                <ChartBar size={16} weight={isActive('/insights') ? 'fill' : 'regular'} />
                <span className="flex-1 text-left">Insights</span>
                {caps.advanced_insights === false && (
                  <Lock size={11} className="text-amber-400/80" data-testid="nav-insights-lock" />
                )}
              </button>
            </div>
          )}

          {/* Projects Section */}
          <div className="mt-6">
            <div className="w-full flex items-center justify-between px-3 py-1 group">
              <button
                onClick={() => navigate('/projects')}
                data-testid="nav-projects-header"
                className="text-[10.5px] font-bold text-[var(--sb-text-dim)] uppercase tracking-[0.14em] hover:text-[var(--sb-text)] transition-colors"
              >Projects</button>
              <div className="flex items-center gap-1">
                {canCreateProject && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <span
                        data-testid="sidebar-projects-add"
                        title="Add project"
                        className="w-5 h-5 flex items-center justify-center rounded hover:bg-[var(--sb-hover)] text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] transition-colors cursor-pointer"
                      >
                        <Plus size={12} weight="bold" />
                      </span>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-48">
                      <DropdownMenuItem data-testid="sidebar-new-project" onClick={onCreateProject} className="text-sm">
                        <Plus size={14} className="mr-2" /> New project
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid="sidebar-import-jira" onClick={() => navigate('/projects?import=jira')} className="text-sm">
                        <Plugs size={14} className="mr-2" /> Import from Jira
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <button
                  onClick={() => setProjectsOpen(!projectsOpen)}
                  data-testid="sidebar-projects-toggle"
                  title={projectsOpen ? 'Collapse' : 'Expand'}
                  className="w-5 h-5 flex items-center justify-center text-[var(--sb-text-dim)] hover:text-[var(--sb-text)]"
                >
                  {projectsOpen ? <CaretDown size={10} /> : <CaretRight size={10} />}
                </button>
              </div>
            </div>
            {projectsOpen && (
              <div className="mt-1 space-y-0.5">
                {projects.length === 0 && (
                  <p className="px-3 py-2 text-[11px] text-[var(--sb-text-dim)]">No projects yet</p>
                )}
                {projects.map((p, idx) => {
                  const canManageThisProject =
                    ['owner', 'admin'].includes(memberRole) ||
                    (memberRole === 'project_manager');
                  const active = isActive(`/projects/${p.project_id}`) || p.project_id === activeTaskProjectId;
                  return (
                    <div key={p.project_id} data-testid={`sidebar-project-row-${p.project_id}`}
                      className={`group relative w-full flex items-center rounded-md transition-colors duration-150
                        ${active ? 'bg-[var(--sb-accent-bg)] text-[var(--sb-accent)] font-semibold ring-1 ring-inset ring-blue-400/30' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                      {active && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-blue-400" aria-hidden />}
                      <button data-testid={`sidebar-project-${p.project_id}`}
                        onClick={() => navigate(`/projects/${p.project_id}`)}
                        title={p.name}
                        className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-[6px] pr-9 text-[13px]">
                        {p.client ? (
                          <ClientLogo client={p.client} size={16} rounded="rounded-[3px]" />
                        ) : (
                          <div className={`w-4 h-4 rounded-[3px] ${getProjectColor(idx)} flex items-center justify-center flex-shrink-0 ring-1 ring-[var(--sb-ring)]`}>
                            <span className="text-[8px] font-bold text-white">{p.key?.charAt(0)}</span>
                          </div>
                        )}
                        <span className="truncate text-left">{p.name}</span>
                      </button>
                      {canManageThisProject && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`sidebar-project-menu-${p.project_id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)] data-[state=open]:bg-[var(--sb-hover)] data-[state=open]:text-[var(--sb-text)] transition-all"
                              title="Project options">
                              <DotsThree size={20} weight="bold" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent side="right" align="start" className="w-48">
                            <DropdownMenuItem
                              onClick={() => navigate(`/projects/${p.project_id}/settings`)}
                              data-testid={`sidebar-project-settings-${p.project_id}`}
                              className="text-sm">
                              <GearSix size={13} className="mr-2" /> Project settings
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => navigate(`/projects/${p.project_id}`)}
                              className="text-sm">
                              <FolderSimple size={13} className="mr-2" /> Open board
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Service Desk Section — visible only when the add-on is enabled
              for this org. Shows a header + Queue + Settings + one row per
              portal. The Sidebar component does NOT enforce capability on
              the routes (pages each call ServiceDeskUpsell when disabled). */}
          {canSeeServiceDesk && (
            <div className="mt-6">
              <button onClick={() => setServiceDeskOpen(!serviceDeskOpen)} className="w-full flex items-center justify-between px-3 py-1 group"
                data-testid="sidebar-service-desk-header">
                <span className="text-[10.5px] font-bold text-[var(--sb-text-dim)] uppercase tracking-[0.14em]">Service Desk</span>
                <span className="w-5 h-5 flex items-center justify-center text-[var(--sb-text-dim)]">
                  {serviceDeskOpen ? <CaretDown size={10} /> : <CaretRight size={10} />}
                </span>
              </button>
              {serviceDeskOpen && (
                <div className="mt-1 space-y-0.5">
                  <button onClick={() => navigate('/service-desk')} data-testid="nav-service-desk-queue"
                    className={`group relative w-full flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] font-medium transition-colors duration-150
                      ${location.pathname === '/service-desk' ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                    {location.pathname === '/service-desk' && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-blue-400" aria-hidden />}
                    <Lifebuoy size={16} weight={location.pathname === '/service-desk' ? 'fill' : 'regular'} />
                    <span className="flex-1 text-left">Queue</span>
                  </button>
                  {portals.map(p => {
                    const active = location.pathname.startsWith(`/service-desk/portal/${p.portal_id}`);
                    return (
                      <button key={p.portal_id} onClick={() => navigate(`/service-desk/portal/${p.portal_id}`)}
                        data-testid={`nav-sd-portal-${p.portal_id}`}
                        title={p.name}
                        className={`group relative w-full flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[13px] font-medium transition-colors duration-150
                          ${active ? 'bg-[var(--sb-accent-bg)] text-[var(--sb-accent)] ring-1 ring-inset ring-blue-400/30' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                        {active && <span className="absolute left-0 top-1 bottom-1 w-[3px] rounded-full bg-blue-400" aria-hidden />}
                        <div className="w-4 h-4 rounded-[3px] bg-blue-500/80 flex items-center justify-center flex-shrink-0 ring-1 ring-[var(--sb-ring)]">
                          <span className="text-[8px] font-bold text-white">{p.key?.charAt(0)}</span>
                        </div>
                        <span className="truncate text-left">{p.name}</span>
                      </button>
                    );
                  })}
                  {['owner', 'admin'].includes(memberRole) && (
                    <>
                      <button onClick={() => navigate('/service-desk/cmdb')} data-testid="nav-service-desk-cmdb"
                        className={`group relative w-full flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[12.5px] transition-colors duration-150
                          ${location.pathname.startsWith('/service-desk/cmdb') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                        <Cube size={13} />
                        <span className="flex-1 text-left">Assets (CMDB)</span>
                      </button>
                      <button onClick={() => navigate('/service-desk/knowledge')} data-testid="nav-service-desk-knowledge"
                        className={`group relative w-full flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[12.5px] transition-colors duration-150
                          ${location.pathname.startsWith('/service-desk/knowledge') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                        <BookOpen size={13} />
                        <span className="flex-1 text-left">Knowledge Base</span>
                      </button>
                      <button onClick={() => navigate('/service-desk/customers')} data-testid="nav-service-desk-customers"
                        className={`group relative w-full flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[12.5px] transition-colors duration-150
                          ${location.pathname.startsWith('/service-desk/customers') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                        <UsersThree size={13} />
                        <span className="flex-1 text-left">User directory</span>
                      </button>
                      <button onClick={() => navigate('/service-desk/help-centres')} data-testid="nav-service-desk-help-centres"
                        className={`group relative w-full flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[12.5px] transition-colors duration-150
                          ${location.pathname.startsWith('/service-desk/help-centres') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                        <Buildings size={13} />
                        <span className="flex-1 text-left">Help Centres</span>
                      </button>
                      <button onClick={() => navigate('/service-desk/settings')} data-testid="nav-service-desk-settings"
                        className={`group relative w-full flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[12.5px] transition-colors duration-150
                          ${location.pathname.startsWith('/service-desk/settings') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-dim)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                        <GearSix size={13} />
                        <span className="flex-1 text-left">Settings</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Team — entire section hidden from contributors */}
          {!isContributor && (
            <div className="mt-6">
              <span className="px-3 text-[10.5px] font-bold text-[var(--sb-text-dim)] uppercase tracking-[0.14em]">Team</span>
              <div className="mt-1">
                <button data-testid="nav-team" onClick={() => navigate('/team')}
                  className={`group relative w-full flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] font-medium transition-colors duration-150
                    ${isActive('/team') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                  {isActive('/team') && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-blue-400" aria-hidden />}
                  <UsersThree size={16} weight={isActive('/team') ? 'fill' : 'regular'} /> Members
                </button>
                <button data-testid="nav-contact-support" onClick={() => navigate('/contact-support')}
                  className={`group relative w-full flex items-center gap-2.5 px-3 py-[7px] rounded-md text-[13px] font-medium transition-colors duration-150
                    ${isActive('/contact-support') ? 'bg-[var(--sb-active-bg)] text-[var(--sb-text)]' : 'text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] hover:bg-[var(--sb-hover)]'}`}>
                  {isActive('/contact-support') && <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] rounded-full bg-blue-400" aria-hidden />}
                  <Lifebuoy size={16} weight={isActive('/contact-support') ? 'fill' : 'regular'} />
                  <span className="flex-1 text-left">Contact Support</span>
                  {caps.contact_support === false && (
                    <Lock size={11} className="text-amber-400/80" data-testid="nav-contact-support-lock" />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User & Workspace footer — consolidates account, workspace identity,
          settings, billing and sign-out behind a single avatar pill so the
          top of the sidebar stays clean. Workspace name lives here as the
          subtitle (Linear / Notion pattern). */}
      <div className="p-2 border-t border-[var(--sb-border)] bg-[var(--sb-surface)] space-y-1">
        {/* Upgrade pill — auto-hides on Business plan. */}
        <div className="px-2 pb-1"><UpgradeButton size="sm" className="w-full justify-center" /></div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-[var(--sb-hover)] transition-colors duration-150">
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-[11px] font-semibold">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <p className="text-[12.5px] font-semibold text-[var(--sb-text)] truncate leading-tight" data-testid="user-menu-name">{user?.name || 'Account'}</p>
                <p className="text-[10.5px] text-[var(--sb-text-dim)] truncate leading-tight mt-0.5" data-testid="user-menu-workspace">
                  {currentOrg?.org_name || 'Workspace'}
                  {memberRole && <span className="ml-1 text-[var(--sb-text-dim)]">· {memberRole.replace('_', ' ')}</span>}
                </p>
              </div>
              <CaretUpDown size={12} className="text-[var(--sb-text-dim)] flex-shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-60">
            <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold px-2 pt-1.5 pb-1">
              Workspace
            </DropdownMenuLabel>
            <div className="px-2 pb-1.5 flex items-center gap-2">
              <div className="w-7 h-7 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
                {currentOrg?.org_name?.charAt(0)?.toUpperCase() || 'W'}
              </div>
              <div className="min-w-0">
                <p className="text-[12.5px] font-semibold text-zinc-900 truncate leading-tight">{currentOrg?.org_name}</p>
                <p className="text-[10.5px] text-zinc-500 truncate leading-tight">Signed in as {user?.email}</p>
              </div>
            </div>
            {otherOrgs.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 font-bold px-2 pt-1 pb-0.5">
                  Switch workspace
                </DropdownMenuLabel>
                {otherOrgs.map(o => (
                  <DropdownMenuItem
                    key={o.org_id}
                    onClick={() => switchOrg(o.org_id)}
                    className="text-sm flex items-center gap-2"
                    data-testid={`switch-org-${o.org_id}`}
                  >
                    <div className="w-5 h-5 rounded bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                      {o.org_name?.charAt(0)?.toUpperCase() || 'W'}
                    </div>
                    <span className="truncate">{o.org_name}</span>
                  </DropdownMenuItem>
                ))}
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/account')} className="text-sm" data-testid="user-menu-account"><UserCircle size={14} className="mr-2" /> My Account</DropdownMenuItem>
            {['owner', 'admin'].includes(currentOrg?.role) && (
              <DropdownMenuItem onClick={() => navigate('/settings')} className="text-sm" data-testid="user-menu-settings"><GearSix size={14} className="mr-2" /> Workspace Settings</DropdownMenuItem>
            )}
            {['owner', 'admin'].includes(currentOrg?.role) && (
              <DropdownMenuItem onClick={() => navigate('/billing')} className="text-sm" data-testid="nav-billing"><CreditCard size={14} className="mr-2" /> Billing &amp; Plans</DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] text-zinc-400 font-normal py-1">Appearance</DropdownMenuLabel>
            {[{ key: 'light', label: 'Light', Icon: Sun }, { key: 'branded', label: 'Branded', Icon: Sparkle }].map(opt => (
              <DropdownMenuItem key={opt.key} onClick={(e) => { e.preventDefault(); setTheme(opt.key); }} className="text-sm" data-testid={`sidebar-theme-${opt.key}`}>
                <opt.Icon size={14} className="mr-2" /> {opt.label}
                {theme === opt.key && <Check size={13} weight="bold" className="ml-auto text-blue-600" />}
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="user-menu-logout" onClick={logout} className="text-red-600 focus:text-red-700 text-sm"><SignOut size={14} className="mr-2" /> Sign Out</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
